import { useEffect, useRef, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { ArrowLeft, ArrowUp, CheckCircle2, Trash2, Send, ImagePlus, X, Loader2, MessageCircle } from "lucide-react";
import { toast } from "sonner";
import ReactMarkdown from "react-markdown";
import { formatRelative } from "@/lib/relative";

export default function ForumThread() {
  const { threadId } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [reply, setReply] = useState("");
  const [replyImg, setReplyImg] = useState(null);
  const [sending, setSending] = useState(false);
  const fileRef = useRef();

  const load = () => {
    setLoading(true);
    api.get(`/forum/threads/${threadId}`)
      .then((r) => setData(r.data))
      .catch(() => toast.error("Kunde inte ladda tråden"))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, [threadId]);

  const voteThread = async () => {
    if (!user) return nav("/logga-in", { state: { from: `/forum/${threadId}` } });
    try {
      const r = await api.post(`/forum/threads/${threadId}/vote`);
      setData((d) => ({ ...d, thread: { ...d.thread, votes: r.data.votes, voted: r.data.voted } }));
    } catch { toast.error("Misslyckades"); }
  };

  const voteReply = async (rid) => {
    if (!user) return nav("/logga-in", { state: { from: `/forum/${threadId}` } });
    try {
      const r = await api.post(`/forum/replies/${rid}/vote`);
      setData((d) => ({
        ...d,
        replies: d.replies.map((x) => x.reply_id === rid ? { ...x, votes: r.data.votes, voted: r.data.voted } : x),
      }));
    } catch { toast.error("Misslyckades"); }
  };

  const markBest = async (rid) => {
    try {
      const r = await api.post(`/forum/threads/${threadId}/best/${rid}`);
      setData((d) => ({ ...d, thread: { ...d.thread, best_reply_id: r.data.best_reply_id } }));
    } catch { toast.error("Misslyckades"); }
  };

  const uploadImg = async (file) => {
    const fd = new FormData();
    fd.append("file", file);
    try {
      const r = await api.post("/uploads/image", fd);
      setReplyImg(r.data.url);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Bilduppladdning misslyckades");
    }
  };

  const submitReply = async (e) => {
    e?.preventDefault();
    if (reply.trim().length < 2) return toast.warning("Skriv ett svar.");
    setSending(true);
    try {
      const r = await api.post(`/forum/threads/${threadId}/replies`, { body: reply.trim(), image_url: replyImg });
      setData((d) => ({ ...d, replies: [...d.replies, r.data] }));
      setReply(""); setReplyImg(null);
      toast.success("Svar publicerat");
    } catch {
      toast.error("Kunde inte publicera svar");
    } finally { setSending(false); }
  };

  const removeThread = async () => {
    if (!confirm("Ta bort denna tråd?")) return;
    try { await api.delete(`/forum/threads/${threadId}`); toast.success("Borttagen"); nav("/forum"); }
    catch { toast.error("Misslyckades"); }
  };

  const removeReply = async (rid) => {
    if (!confirm("Ta bort detta svar?")) return;
    try {
      await api.delete(`/forum/replies/${rid}`);
      setData((d) => ({ ...d, replies: d.replies.filter((x) => x.reply_id !== rid) }));
    } catch { toast.error("Misslyckades"); }
  };

  if (loading || !data) return <div className="text-muted-foreground">Laddar…</div>;
  const { thread, replies } = data;
  const canModerate = user && (user.user_id === thread.author?.user_id || user.role === "admin");

  return (
    <div className="max-w-3xl mx-auto" data-testid="thread-page">
      <Link to="/forum" className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-primary mb-5">
        <ArrowLeft className="size-4" />Tillbaka till forumet
      </Link>

      <Card className="p-6 bg-card mb-6">
        <h1 className="font-serif text-3xl font-medium tracking-tight mb-2">{thread.title}</h1>
        <div className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground mb-4">
          <strong className="text-foreground">{thread.author.name}</strong>
          {thread.author.profession && <span>· {thread.author.profession}</span>}
          <span>·</span>
          <span>{formatRelative(thread.created_at)}</span>
          {thread.best_reply_id && <Badge variant="outline" className="border-primary/50 text-primary">Löst</Badge>}
        </div>

        <div className="markdown-body text-sm" data-testid="thread-body"><ReactMarkdown>{thread.body}</ReactMarkdown></div>
        {thread.image_url && <img src={thread.image_url} alt="" className="mt-4 max-h-72 rounded border border-border" />}

        <div className="flex items-center gap-3 mt-5 pt-4 border-t border-border">
          <button onClick={voteThread} className={`flex items-center gap-1.5 text-sm px-3 py-1.5 rounded-md border ${thread.voted ? "border-primary text-primary" : "border-border text-muted-foreground hover:border-primary/50"}`} data-testid="thread-vote">
            <ArrowUp className="size-4" />{thread.votes}
          </button>
          <span className="text-xs text-muted-foreground flex items-center gap-1"><MessageCircle className="size-3.5" /> {replies.length} svar</span>
          <div className="flex-1" />
          {canModerate && (
            <button onClick={removeThread} className="text-xs text-muted-foreground hover:text-destructive flex items-center gap-1" data-testid="thread-delete">
              <Trash2 className="size-3.5" />Ta bort
            </button>
          )}
        </div>
      </Card>

      <h2 className="font-serif text-xl font-medium mb-3">
        <span className="title-rule" />{replies.length} svar
      </h2>

      <div className="space-y-3 mb-6" data-testid="reply-list">
        {replies.map((r) => {
          const isBest = thread.best_reply_id === r.reply_id;
          return (
            <Card key={r.reply_id} className={`p-5 bg-card ${isBest ? "border-primary/50" : ""}`} data-testid={`reply-${r.reply_id}`}>
              <div className="flex gap-4">
                <div className="flex flex-col items-center gap-1">
                  <button onClick={() => voteReply(r.reply_id)} className={`flex flex-col items-center text-sm ${r.voted ? "text-primary" : "text-muted-foreground hover:text-primary"}`} data-testid={`reply-vote-${r.reply_id}`}>
                    <ArrowUp className="size-5" />
                    <span className="font-serif text-base">{r.votes}</span>
                  </button>
                  {thread.is_author && (
                    <button
                      onClick={() => markBest(r.reply_id)}
                      title={isBest ? "Avmarkera som bästa svar" : "Markera som bästa svar"}
                      data-testid={`mark-best-${r.reply_id}`}
                      className={isBest ? "text-primary" : "text-muted-foreground hover:text-primary"}
                    >
                      <CheckCircle2 className={`size-5 ${isBest ? "fill-primary text-primary-foreground" : ""}`} />
                    </button>
                  )}
                  {!thread.is_author && isBest && <CheckCircle2 className="size-5 text-primary" />}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 text-xs text-muted-foreground mb-2">
                    <strong className="text-foreground">{r.author.name}</strong>
                    {r.author.profession && <span>· {r.author.profession}</span>}
                    {r.is_thread_author && <Badge variant="secondary" className="text-[9px] font-mono">FRÅGESTÄLLAREN</Badge>}
                    {isBest && <Badge variant="outline" className="text-[9px] font-mono border-primary/50 text-primary">BÄSTA SVAR</Badge>}
                    <span>· {formatRelative(r.created_at)}</span>
                  </div>
                  <div className="markdown-body text-sm"><ReactMarkdown>{r.body}</ReactMarkdown></div>
                  {r.image_url && <img src={r.image_url} alt="" className="mt-3 max-h-56 rounded border border-border" />}
                  {(r.is_author || user?.role === "admin") && (
                    <button onClick={() => removeReply(r.reply_id)} className="mt-3 text-xs text-muted-foreground hover:text-destructive flex items-center gap-1">
                      <Trash2 className="size-3" />Ta bort
                    </button>
                  )}
                </div>
              </div>
            </Card>
          );
        })}
      </div>

      {user ? (
        <Card className="p-4 bg-card">
          <form onSubmit={submitReply} className="space-y-3">
            <label className="text-[11px] font-mono uppercase tracking-widest text-muted-foreground block">Ditt svar (markdown stöds)</label>
            <Textarea value={reply} onChange={(e) => setReply(e.target.value)} rows={5} className="resize-none" data-testid="reply-input" />
            {replyImg ? (
              <div className="relative inline-block">
                <img src={replyImg} alt="" className="max-h-32 rounded border border-border" />
                <button type="button" onClick={() => setReplyImg(null)} className="absolute -top-2 -right-2 size-5 bg-destructive text-destructive-foreground rounded-full flex items-center justify-center"><X className="size-3" /></button>
              </div>
            ) : (
              <Button type="button" variant="outline" size="sm" onClick={() => fileRef.current?.click()}>
                <ImagePlus className="size-4 mr-2" />Bifoga bild
              </Button>
            )}
            <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={(e) => e.target.files?.[0] && uploadImg(e.target.files[0])} />
            <Button type="submit" disabled={sending} data-testid="reply-submit">
              {sending ? <Loader2 className="size-4 animate-spin mr-2" /> : <Send className="size-4 mr-2" />}Skicka svar
            </Button>
          </form>
        </Card>
      ) : (
        <Card className="p-6 text-center bg-card border-dashed">
          <p className="text-sm text-muted-foreground mb-3">Logga in för att svara på tråden.</p>
          <Button onClick={() => nav("/logga-in", { state: { from: `/forum/${threadId}` } })}>Logga in</Button>
        </Card>
      )}
    </div>
  );
}
